import React, { useState, useEffect } from 'react';
import { Clock, Zap, X } from 'lucide-react';
import { Badge, Container, accent, cn } from '../ui';

interface BannerProps {
    title: string;
    countdownDate: Date;
    thumbnailOverlay?: string;
}

interface TimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

const CountdownBanner: React.FC<BannerProps> = ({ title, countdownDate, thumbnailOverlay }) => {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsOpen(true);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    function calculateTimeLeft(): TimeLeft {
        const difference = +new Date(countdownDate) - +new Date();

        if (difference > 0) {
            return {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        }

        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [countdownDate]);

    const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

    if (isExpired) {
        return null;
    }

    const handleClose = () => {
        setIsOpen(false);
    };

    if (!isOpen) {
        return null;
    }

    const CountdownDigit = ({ value, label }: { value: number; label: string }) => {
        return (
            <div className="flex min-w-[3.5rem] flex-col items-center px-3">
                <span className="font-display text-h3 tabular-nums text-text-primary">
                    {String(value).padStart(2, '0')}
                </span>
                <span className="mt-0.5 text-overline font-semibold uppercase text-text-secondary">
                    {label}
                </span>
            </div>
        );
    };

    return (
        // A band, not a modal: it announces the event without trapping the page
        // behind a backdrop, and it can be dismissed with one tap.
        //
        // It sits directly above the hero, which is off-white, so the gold
        // surface plus the full-strength gold rule is what makes it register as
        // an announcement rather than as more page chrome.
        <aside
            aria-label="Event announcement"
            className="relative z-sticky animate-fade-up border-b border-border bg-surface-gold"
        >
            <span aria-hidden="true" className={cn('block h-1', accent('gold').rule)} />
            <Container className="flex flex-col gap-5 py-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
                <div className="flex items-start gap-4">
                    {thumbnailOverlay && (
                        <img
                            src={thumbnailOverlay}
                            alt=""
                            loading="lazy"
                            className="hidden h-16 w-24 shrink-0 rounded-md border border-border object-cover sm:block"
                        />
                    )}
                    <div className="min-w-0">
                        <Badge tone="accent">
                            <Zap size={16} aria-hidden="true" />
                            Happening Now!!!
                        </Badge>
                        {/* Deliberately not a heading: the banner sits above the
                            page h1, so a heading here would break the order. */}
                        <p className="mt-2 text-h4 font-sans font-semibold text-text-primary">
                            {title}
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-4 lg:justify-end">
                    <div className="flex items-center gap-3">
                        <span className="hidden items-center gap-1.5 text-caption text-text-secondary sm:flex">
                            <Clock size={16} aria-hidden="true" />
                            Live Countdown
                        </span>
                        <div className="flex divide-x divide-border rounded-md border border-border bg-background-card py-3">
                            <CountdownDigit value={timeLeft.days} label="Days" />
                            <CountdownDigit value={timeLeft.hours} label="Hours" />
                            <CountdownDigit value={timeLeft.minutes} label="Minutes" />
                            <CountdownDigit value={timeLeft.seconds} label="Seconds" />
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Dismiss announcement"
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary transition-colors duration-200 hover:bg-background-card hover:text-text-primary"
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>
            </Container>
        </aside>
    );
};

export default CountdownBanner;
